import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Globe, Instagram, Mail, ArrowRight, AlertCircle, X, HelpCircle, User } from 'lucide-react';

const LinksPage: React.FC = () => {
  // Estado para controlar a exibição da FAQ
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAboutMe, setShowAboutMe] = useState(false);
  // Animações
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1, // Reduzido para transição mais rápida
        delayChildren: 0.05,  // Reduzido para começar mais cedo
        ease: "easeOut"      // Adicionado para maior suavidade
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { 
      y: 0, 
      opacity: 1, 
      transition: { 
        type: 'spring', 
        damping: 15,     // Aumentado para reduzir oscilação
        stiffness: 120,  // Adicionado para resposta mais rápida
        mass: 0.8        // Massa menor para movimento mais leve
      } 
    }
  };

  const buttonVariants = {
    hover: { 
      scale: 1.02,      // Reduzido para ser mais sutil
      y: -2,            // Reduzido para movimento mais contido
      boxShadow: '0 8px 15px rgba(0, 0, 0, 0.08)',
      transition: {
        type: "tween",   // Mudado para tween para movimento mais linear
        ease: "easeOut",
        duration: 0.2    // Acelerado para sensação mais responsiva
      }
    },
    tap: { 
      scale: 0.98,      // Menos compressão para parecer mais responsivo
      transition: {
        type: "spring",
        stiffness: 300,  // Alta rigidez para resposta instantânea
        damping: 15      // Bom amortecimento para evitar oscilação
      }
    }
  };

  // Links
  const links = [
    {
      name: 'Sobre Mim',
      description: 'Conheça meu trabalho',
      icon: <User size={24} className="text-accent" />,
      onClick: () => setShowAboutMe(true),
      isButton: true
    },
    {
      name: 'Conta bloqueada?',
      description: 'Direito Bancário',
      icon: <AlertCircle size={24} className="text-accent" />,
      onClick: () => setShowFAQ(true),
      isButton: true
    },
    {
      name: 'WhatsApp',
      description: 'Fale diretamente conosco',
      icon: <MessageCircle size={24} className="text-accent" />,
      href: 'https://wa.me/5581993507689?text=Olá.%20Dra.%20Lidiane,%20vim%20pelo%20Instagram%20e%20gostaria%20de%20mais%20informações'
    },
    {
      name: 'Website',
      description: 'Visite nosso site oficial',
      icon: <Globe size={24} className="text-accent" />,
      href: '/'
    },
    {
      name: 'Instagram',
      description: 'Acompanhe nossas redes sociais',
      icon: <Instagram size={24} className="text-accent" />,
      href: 'https://www.instagram.com/lidianealmeidaadv?igsh=Z3h6bjhuN2drdmh3&utm_source=qr'
    },
    {
      name: 'E-mail',
      description: 'Entre em contato via e-mail',
      icon: <Mail size={24} className="text-accent" />,
      href: 'mailto:contato@lidianealmeidaadv.com'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col items-center p-4 sm:p-6 pt-0 relative overflow-hidden">
      {/* Fundo com gradient e imagem */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary/95 to-primary/90 -z-20"></div>
      
      {/* Imagem de fundo com opacidade */}
      <div 
        className="absolute inset-0 bg-[url('/dralidiane3.png')] bg-cover bg-center opacity-25 -z-10"
        style={{ backgroundPosition: 'center 20%' }}
      ></div>

      {/* Efeito de partículas decorativas - otimizado para performance */}
      <div className="absolute inset-0 overflow-hidden -z-5">
        {/* Reduzido para 6 partículas para melhorar a performance */}
        {[...Array(6)].map((_, i) => {
          // Pré-calculando valores aleatórios para evitar recálculos desnecessários
          const sizeBase = 15 + (i * 10); // Tamanhos mais previsíveis baseados no índice
          const initialX = (i % 3) * 40 + 10 + '%'; // Distribui as partículas de forma mais uniforme
          const initialY = Math.floor(i / 3) * 40 + 10 + '%';
          const opacityBase = 0.05 + (i * 0.02); // Opacidade progressiva
          
          return (
            <motion.div 
              key={i}
              className="absolute rounded-full bg-white/10"
              initial={{ 
                x: initialX, 
                y: initialY,
                scale: 0.6 + (i * 0.1),
                opacity: opacityBase
              }}
              animate={{ 
                x: [
                  initialX, 
                  parseInt(initialX) + 10 + '%', 
                  initialX
                ],
                y: [
                  initialY, 
                  parseInt(initialY) + 15 + '%', 
                  initialY
                ],
                opacity: [opacityBase, opacityBase + 0.05, opacityBase]
              }}
              transition={{ 
                duration: 20 + (i * 3), // Duração mais longa e estável
                ease: "easeInOut",
                repeat: Infinity, 
                repeatType: "reverse",
                // Reduzindo a carga de CPU com configurações mais leves
                type: "tween" // Tween é mais eficiente que spring para animações contínuas
              }}
              style={{
                width: sizeBase + 'px',
                height: sizeBase + 'px',
                willChange: 'transform, opacity' // Otimização para browsers modernos
              }}
            />
          );
        })}
      </div>

      {/* Modal Sobre Mim */}
      <AnimatePresence mode="wait">
        {showAboutMe && (
          <motion.div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm p-4 sm:p-8 flex items-center justify-center z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div 
              className="bg-primary rounded-2xl p-5 sm:p-6 md:p-8 max-w-lg w-full relative
                sm:max-w-lg sm:rounded-2xl sm:p-6
                max-w-full rounded-none p-0
                sm:static fixed inset-0 z-50"
              style={{ height: '100%', minHeight: '100dvh' }}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-white">Sobre Mim</h2>
                <button 
                  onClick={() => setShowAboutMe(false)}
                  className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>
              </div>
              
              <div className="space-y-4 text-white text-left">
                {/* Forçando alinhamento à esquerda em todos os títulos e parágrafos */}
                <style>{`
                  .about-modal * {
                    text-align: left !important;
                  }
                `}</style>
                <div className="about-modal">
                  <div className="flex flex-col md:flex-row gap-6 items-center mb-6">
                    <div className="w-full md:w-1/3 flex justify-center">
                      <img 
                        src="/dralidiane2.png" 
                        alt="Lidiane Almeida" 
                        className="rounded-xl shadow-lg object-contain h-80 md:h-72 w-full max-w-xs md:max-w-sm opacity-40"
                      />
                    </div>
                    <div className="w-full md:w-2/3">
                      <p className="text-white/90 text-left">
                        Prazer, sou Lidiane Almeida. Especialista em Direito Bancário e Direito do Trabalho, inscrita na OAB sob número 56.912, atuo à nível nacional, de forma totalmente digital para acelerar a resolução das causas e prezar pelo conforto dos meus clientes.
                      </p>
                    </div>
                  </div>
                  
                  <div className="pt-4">
                    <motion.a
                      href="https://wa.me/5581993507689?text=Olá.%20Dra.%20Lidiane,%20gostaria%20de%20falar%20com%20você."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-accent hover:bg-accent/90 text-white font-medium py-3 px-4 rounded-lg w-full flex items-center justify-center gap-2 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <MessageCircle size={20} />
                      Fale comigo
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal da FAQ */}
      <AnimatePresence>
        {showFAQ && (
          <motion.div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="bg-primary/95 backdrop-blur-md border border-white/10 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white flex items-center">
                  <HelpCircle size={20} className="mr-2 text-accent" />
                  Contas Bloqueadas - FAQ
                </h2>
                <button 
                  onClick={() => setShowFAQ(false)}
                  className="text-white/70 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4 text-white text-left">
                {/* Forçando alinhamento à esquerda em todos os títulos e parágrafos */}
                <style>{`
                  .faq-modal * {
                    text-align: left !important;
                  }
                `}</style>
                <div className="faq-modal">
                  <div>
                    <h3 className="font-bold text-accent">Por que minha conta foi bloqueada pela Justiça?</h3>
                    <p className="mt-1 text-white/90">O bloqueio de valores em conta acontece em razão de uma decisão judicial em um processo de execução de dívida, que pode ser cível, trabalhista ou até mesmo fiscal (tributos).</p>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-accent">Eu consigo recuperar os valores rapidamente?</h3>
                    <p className="mt-1 text-white/90">Sim, com a interposição de recurso é possível desbloquear o valor na conta, principalmente nos casos de conta salário ou poupança.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-accent">O bloqueio dos valores acontece apenas uma vez?</h3>
                    <p className="mt-1 text-white/90">Não! Caso você não se manifeste, o juiz determinará que ocorra a tentativa de bloqueio diariamente durante 30 dias até bloquear o valor total devido no processo.</p>
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-accent">Como desbloquear a minha conta?</h3>
                    <p className="mt-1 text-white/90">O pedido de desbloqueio deve ser feito por um advogado no processo pelo qual a ordem de bloqueio foi determinada.</p>
                  </div>
                  
                  <div className="pt-4">
                    <motion.a
                      href="https://wa.me/5581993507689?text=Olá.%20Dra.%20Lidiane,%20estou%20com%20problemas%20de%20conta%20bloqueada%20e%20gostaria%20de%20falar%20com%20um%20especialista."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-accent hover:bg-accent/90 text-white font-medium py-3 px-4 rounded-lg w-full flex items-center justify-center gap-2 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <MessageCircle size={20} />
                      Fale com um especialista
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="w-full max-w-md mx-auto flex flex-col items-center justify-center z-10 relative py-8">
        {/* Logo e cabeçalho - transição otimizada */}
        <motion.div 
          className="mb-8 mt-5 sm:mt-10 text-center"
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.5, 
            ease: "easeOut",
            when: "beforeChildren"
          }}
        >
          <img 
            src="/logo.png" 
            alt="Lidiane Almeida Advogada" 
            className="h-24 sm:h-28 mx-auto mb-4"
          />
          <h1 className="text-white text-2xl sm:text-3xl font-heading font-bold text-shadow">Dra. Lidiane Almeida</h1>
          <p className="text-white/80 mt-2 mb-3">Advogada especialista em desbloqueio de contas</p>
          <div className="w-20 h-1 bg-accent mx-auto rounded-full"></div>
        </motion.div>

        {/* Links */}
        <motion.div 
          className="space-y-4 w-full"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {links.map((link, index) => {
            // Componente comum para ambos os tipos de links
            const LinkContent = () => (
              <>
                <div className="bg-primary/80 p-3 rounded-lg mr-4 shadow-inner">
                  {link.icon}
                </div>
                <div className="flex-1">
                  <span className="font-medium text-lg block">{link.name}</span>
                  <span className="text-white/70 text-sm">{link.description}</span>
                </div>
                <motion.div 
                  className="text-accent"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ 
                    repeat: Infinity, 
                    duration: 1.2, 
                    repeatDelay: 1.5,
                    ease: "easeInOut",
                    type: "tween"
                  }}
                >
                  <ArrowRight size={20} />
                </motion.div>
              </>
            );
            
            // Renderiza um botão ou um link dependendo do tipo
            return link.isButton ? (
              <motion.button
                key={index}
                onClick={link.onClick}
                className="flex items-center p-5 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/15 text-white shadow-lg w-full transition-all duration-300 border border-white/10 text-left"
                variants={item}
                whileHover={buttonVariants.hover}
                whileTap={buttonVariants.tap}
              >
                <div className="flex items-center w-full">
                  <div className="bg-primary/80 p-3 rounded-lg mr-4 shadow-inner">
                    {link.icon}
                  </div>
                  <div className="flex-1">
                    <span className="font-medium text-lg block text-left">{link.name}</span>
                    <span className="text-white/70 text-sm block text-left">{link.description}</span>
                  </div>
                  <motion.div 
                    className="text-accent"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ 
                      repeat: Infinity, 
                      duration: 1.2, 
                      repeatDelay: 1.5,
                      ease: "easeInOut",
                      type: "tween"
                    }}
                  >
                    <ArrowRight size={20} />
                  </motion.div>
                </div>
              </motion.button>
            ) : (
              <motion.a
                key={index}
                href={link.href}
                target={link.href === '/' ? '_self' : '_blank'}
                rel="noopener noreferrer"
                className="flex items-center p-5 rounded-xl bg-white/10 backdrop-blur-sm hover:bg-white/15 text-white shadow-lg w-full transition-all duration-300 border border-white/10"
                variants={item}
                whileHover={buttonVariants.hover}
                whileTap={buttonVariants.tap}
              >
                <LinkContent />
              </motion.a>
            );
          })}
        </motion.div>

        {/* Rodapé - animação otimizada */}
        <motion.div 
          className="mt-12 mb-4 text-center bg-primary/30 backdrop-blur-sm p-3 rounded-lg border border-white/5"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            delay: 0.5, // Iniciando mais cedo
            duration: 0.4, // Duração mais curta
            ease: "easeOut" // Curva de easing mais suave
          }}
        >
          <p className="text-white/70 text-sm">
            &copy; {new Date().getFullYear()} - Dra. Lidiane Almeida - OAB 56.912
          </p>
        </motion.div>
      </div>
      {/* Crédito Nsistemas */}
      <div className="w-full text-center mt-2 mb-2 z-10 relative">
        <a
          href="https://wa.me/5562999930796?text=Ol%C3%A1.%20Vi%20o%20site%20da%20Dra.%20Lidiane.%20Gostaria%20de%20informa%C3%A7%C3%B5es%20sobre%20seus%20sistemas."
          target="_blank"
          rel="noopener noreferrer"
          className="text-white/40 text-xs hover:text-accent transition-colors"
        >
          Desenvolvido por Nsistemas
        </a>
      </div>
    </div>
  );
};

export default LinksPage;
